import * as React from "react";
import { useMemo, useState, useCallback, useRef } from "react";
import styled, { css } from "styled-components";
import { ButtonGroup } from "components/bootstrap";
import ColumnsVisibilitySelect from "components/common/EntityDataTable/ColumnsVisibilitySelect";
import { PageSizeSelect } from "components/common";
import SelectedEntitiesProvider from "components/common/EntityDataTable/contexts/SelectedEntitiesProvider";
import MetaDataProvider from "components/common/EntityDataTable/contexts/MetaDataProvider";
import useTable from "components/common/EntityDataTable/hooks/useTable";
import useElementWidths from "components/common/EntityDataTable/hooks/useElementWidths";
import useVisibleColumnOrder from "components/common/EntityDataTable/hooks/useVisibleColumnOrder";
import TableDndProvider from "components/common/EntityDataTable/TableDndProvider";
import Table from "components/common/EntityDataTable/Table";
import DndStylesContext from "components/common/EntityDataTable/contexts/DndStylesContext";
import {
  actionsHeaderWidthVar,
  columnOpacityVar,
  columnTransition,
  columnTransformVar,
  columnWidthVar,
  displayScrollRightIndicatorVar,
  scrollContainerWidthVar
} from "components/common/EntityDataTable/CSSVariables";
import useHeaderMinWidths from "components/common/EntityDataTable/hooks/useHeaderMinWidths";
import useColumnDefinitions from "components/common/EntityDataTable/hooks/useColumnDefinitions";
import useColumnRenderers from "components/common/EntityDataTable/hooks/useColumnRenderers";
import useAuthorizedColumnSchemas from "components/common/EntityDataTable/hooks/useAuthorizedColumnSchemas";
import useIntersectionObserver from "hooks/useIntersectionObserver";
import { CELL_PADDING } from "components/common/EntityDataTable/Constants";
import ActiveSliceColContext from "components/common/EntityDataTable/contexts/ActiveSliceColContext";
import ExpandedSectionsProvider from "./contexts/ExpandedSectionsProvider";
import BulkActionsRow from "./BulkActionsRow";
const cssVariable = (variable, value) => css`
  ${variable}: ${value};
`;
const ScrollContainer = styled.div(
  ({
    $columnWidths,
    $activeColId,
    $columnTransform,
    $actionsHeaderWidth,
    $canScrollRight,
    $scrollContainerWidth
  }) => css`
    width: 100%;
    overflow-x: auto;

    ${Object.entries($columnWidths).map(([id, width]) => cssVariable(columnWidthVar(id), `${width}px`))}
    ${Object.entries($columnTransform).map(([id, transform]) => cssVariable(columnTransformVar(id), transform))}
    ${$actionsHeaderWidth ? cssVariable(actionsHeaderWidthVar, `${$actionsHeaderWidth}px`) : ""}
    ${$canScrollRight ? cssVariable(displayScrollRightIndicatorVar, "block") : ""}
    ${$scrollContainerWidth ? cssVariable(scrollContainerWidthVar, `${$scrollContainerWidth}px`) : ""}
    ${$activeColId ? css`
          ${cssVariable(columnOpacityVar($activeColId), 0.4)}
          ${cssVariable(columnTransition(), "transform 0.2s ease-in-out")}
        ` : ""}
  `
);
const InnerContainer = styled.div`
  position: relative;
  height: 100%;
  width: fit-content;
`;
const ScrollRightIndicator = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: ${CELL_PADDING}px;
  pointer-events: none;
  z-index: 2;
`;
const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  min-height: 22px;
  width: 100%;
`;
const LayoutConfigRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;
const EntityDataTable = ({
  activeSort = void 0,
  activeSliceCol = void 0,
  bulkSelection: { actions, onChangeSelection, initialSelection, isEntitySelectable } = {},
  columnRenderers: customColumnRenderers = void 0,
  columnSchemas,
  defaultColumnOrder,
  defaultDisplayedColumns,
  entities,
  entityActions = void 0,
  entityAttributesAreCamelCase,
  enableSlicing = false,
  expandedSectionRenderers = void 0,
  rowOverride = void 0,
  layoutPreferences,
  meta = void 0,
  onChangeSlicing,
  onLayoutPreferencesChange,
  onPageSizeChange = void 0,
  onResetLayoutPreferences,
  onSortChange,
  pageSize = void 0,
  parentBgColor = void 0,
  appSection = void 0,
  noColumnReordering = false,
  noPageSizeSelect = false
}) => {
  var _a;
  const [selectedEntities, setSelectedEntities] = useState(initialSelection != null ? initialSelection : []);
  const hasRowActions = typeof entityActions === "function";
  const displayBulkAction = !!actions;
  const displayBulkSelectCol = typeof onChangeSelection === "function" || displayBulkAction;
  const displayPageSizeSelect = typeof onPageSizeChange === "function";
  const authorizedColumnSchemas = useAuthorizedColumnSchemas(columnSchemas);
  const columnRenderersByAttribute = useColumnRenderers(authorizedColumnSchemas, customColumnRenderers);
  const { headerMinWidths, handleHeaderSectionResize } = useHeaderMinWidths();
  const scrollContainerRef = useRef(null);
  const scrolledToRightIndicator = useRef();
  const scrolledToRight = useIntersectionObserver(scrollContainerRef, scrolledToRightIndicator);
  const [internalAttributeColumnOrder, setInternalAttributeColumnOrder] = useState(
    (_a = layoutPreferences == null ? void 0 : layoutPreferences.order) != null ? _a : defaultColumnOrder
  );
  const [internalColumnWidthPreferences, setInternalColumnWidthPreferences] = useState(
    () => {
      var _a2;
      return Object.fromEntries(
        Object.entries((_a2 = layoutPreferences == null ? void 0 : layoutPreferences.attributes) != null ? _a2 : {}).flatMap(
          ([key, { width }]) => typeof width === "number" ? [[key, width]] : []
        )
      );
    }
  );
  const columnOrder = useVisibleColumnOrder(
    layoutPreferences == null ? void 0 : layoutPreferences.attributes,
    internalAttributeColumnOrder,
    defaultDisplayedColumns,
    displayBulkSelectCol
  );
  const { columnWidths, handleActionsWidthChange, tableIsCompressed, actionsColMinWidth, scrollContainerWidth } = useElementWidths({
    columnRenderersByAttribute,
    columnSchemas: authorizedColumnSchemas,
    columnWidthPreferences: internalColumnWidthPreferences,
    displayBulkSelectCol,
    entities,
    hasRowActions,
    headerMinWidths,
    scrollContainerRef,
    visibleColumns: columnOrder
  });
  const columnDefinitions = useColumnDefinitions({
    actionsColMinWidth,
    columnRenderersByAttribute,
    columnSchemas: authorizedColumnSchemas,
    columnWidths,
    displayBulkSelectCol,
    enableSlicing,
    entityActions,
    entityAttributesAreCamelCase,
    hasRowActions,
    meta,
    onActionsWidthChange: handleActionsWidthChange,
    onChangeSlicing,
    onHeaderSectionResize: handleHeaderSectionResize,
    parentBgColor,
    appSection
  });
  const table = useTable({
    columnOrder,
    columnWidths,
    columnDefinitions,
    defaultColumnOrder,
    displayBulkSelectCol,
    entities,
    headerMinWidths,
    internalColumnWidthPreferences,
    isEntitySelectable,
    layoutPreferences,
    onChangeSelection,
    onLayoutPreferencesChange,
    onSortChange,
    selectedEntities,
    setInternalAttributeColumnOrder,
    setInternalColumnWidthPreferences,
    setSelectedEntities,
    sort: activeSort
  });
  const headerGroups = useMemo(() => table.getHeaderGroups(), [columnOrder]);
  const resetLayoutPreferences = useCallback(() => {
    onResetLayoutPreferences().then(() => {
      setInternalAttributeColumnOrder(defaultColumnOrder);
      setInternalColumnWidthPreferences({});
    });
  }, [defaultColumnOrder, onResetLayoutPreferences]);
  return /* @__PURE__ */ React.createElement(MetaDataProvider, { meta }, /* @__PURE__ */ React.createElement(
    SelectedEntitiesProvider,
    {
      table,
      selectedEntities,
      isSomeRowsSelected: table.getIsSomeRowsSelected(),
      isAllRowsSelected: table.getIsAllRowsSelected()
    },
    /* @__PURE__ */ React.createElement(ActiveSliceColContext.Provider, { value: activeSliceCol }, /* @__PURE__ */ React.createElement(ExpandedSectionsProvider, null, /* @__PURE__ */ React.createElement(ActionsRow, null, /* @__PURE__ */ React.createElement("div", null, displayBulkAction && /* @__PURE__ */ React.createElement(BulkActionsRow, { bulkActions: actions })), noColumnReordering && noPageSizeSelect ? null : /* @__PURE__ */ React.createElement(LayoutConfigRow, null, "Show", /* @__PURE__ */ React.createElement(ButtonGroup, null, displayPageSizeSelect && !noPageSizeSelect && /* @__PURE__ */ React.createElement(PageSizeSelect, { pageSize, showLabel: false, onChange: onPageSizeChange }), !noColumnReordering && /* @__PURE__ */ React.createElement(
      ColumnsVisibilitySelect,
      {
        table,
        onResetLayoutPreferences: resetLayoutPreferences
      }
    )))), /* @__PURE__ */ React.createElement(TableDndProvider, { table }, /* @__PURE__ */ React.createElement(DndStylesContext.Consumer, null, ({ activeColId, columnTransform }) => /* @__PURE__ */ React.createElement(
      ScrollContainer,
      {
        id: "scroll-container",
        ref: scrollContainerRef,
        $actionsHeaderWidth: actionsColMinWidth,
        $activeColId: activeColId,
        $columnTransform: columnTransform,
        $columnWidths: columnWidths,
        $canScrollRight: scrolledToRight && tableIsCompressed,
        $scrollContainerWidth: scrollContainerWidth
      },
      /* @__PURE__ */ React.createElement(InnerContainer, null, /* @__PURE__ */ React.createElement(
        Table,
        {
          expandedSectionRenderers,
          headerGroups,
          rowOverride,
          rows: table.getRowModel().rows
        }
      ), /* @__PURE__ */ React.createElement(ScrollRightIndicator, { ref: scrolledToRightIndicator }))
    )))))
  ));
};
export default EntityDataTable;
